import { Children, cloneElement, isValidElement, type PropsWithChildren } from 'react'
import { StyleSheet, View, type ViewProps } from 'react-native'
import { Box } from './ui/box'

const gridSpaceClassName = {
  xs: '-m-0.5',
  sm: '-m-1',
  md: '-m-1.5',
  lg: '-m-2',
  xl: '-m-2.5',
  '2xl': '-m-3',
  '3xl': '-m-3.5',
  '4xl': '-m-4',
}

const gridItemSpaceClassName = {
  xs: 'p-0.5',
  sm: 'p-1',
  md: 'p-1.5',
  lg: 'p-2',
  xl: 'p-2.5',
  '2xl': 'p-3',
  '3xl': 'p-3.5',
  '4xl': 'p-4',
}

type GridProps = ViewProps & {
  space?: keyof typeof gridSpaceClassName
  cols?: number
}

export function Grid({ className, children, space, cols = 4, ...viewProps }: PropsWithChildren<GridProps>) {
  const childClassName = space ? gridItemSpaceClassName[space] : ''
  // NativeWind only compiles literal class names, so a runtime `w-1/${cols}` would not apply
  const childStyle = { width: cols > 0 ? (`${100 / cols}%` as const) : '100%' } as const

  const childrenWithProps = Children.map(children, (child, index) => {
    if (isValidElement(child)) {
      return cloneElement(child, {
        className: `${childClassName} ${(child.props as any)?.className ?? ''}`,
        // GridItem is a plain <div> on web, which can't take a style array
        style: StyleSheet.flatten([childStyle, (child.props as GridItemProps).style]),
      } as Partial<GridItemProps>)
    }

    return child
  })
  return (
    <View
      className={`w-full flex-row flex-wrap justify-start ${space ? gridSpaceClassName[space] : ''} ${className ?? ''}`}
      {...viewProps}
    >
      {childrenWithProps}
    </View>
  )
}

type GridItemProps = ViewProps

export function GridItem({ children, ...viewProps }: PropsWithChildren<GridItemProps>) {
  return <Box {...viewProps}>{children}</Box>
}
